<template>
  <div class="checkout-page">
    <section class="checkout-header">
      <div class="container">
        <h1>Checkout</h1>
        <p>Complete your order</p>
      </div>
    </section>

    <section class="checkout-content">
      <div class="container">
        <div class="checkout-layout">
          <!-- Checkout Form -->
          <div class="checkout-form">
            <!-- Shipping Information -->
            <div class="form-section">
              <h2>Shipping Information</h2>
              <form @submit.prevent="processOrder">
                <div class="form-row">
                  <div class="form-group">
                    <label for="firstName">First Name *</label>
                    <input 
                      type="text" 
                      id="firstName" 
                      v-model="shippingInfo.firstName" 
                      required
                    >
                  </div>
                  <div class="form-group">
                    <label for="lastName">Last Name *</label>
                    <input 
                      type="text" 
                      id="lastName" 
                      v-model="shippingInfo.lastName" 
                      required
                    >
                  </div>
                </div>

                <div class="form-group">
                  <label for="email">Email Address *</label>
                  <input 
                    type="email" 
                    id="email" 
                    v-model="shippingInfo.email" 
                    required
                  >
                </div>

                <div class="form-group">
                  <label for="phone">Phone Number *</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    v-model="shippingInfo.phone" 
                    required
                  >
                </div>

                <div class="form-group">
                  <label for="address">Street Address *</label>
                  <input 
                    type="text" 
                    id="address" 
                    v-model="shippingInfo.address" 
                    required
                  >
                </div>

                <div class="form-row">
                  <div class="form-group">
                    <label for="city">City *</label>
                    <input 
                      type="text" 
                      id="city" 
                      v-model="shippingInfo.city" 
                      required
                    >
                  </div>
                  <div class="form-group">
                    <label for="state">State *</label>
                    <input 
                      type="text" 
                      id="state" 
                      v-model="shippingInfo.state" 
                      required
                    >
                  </div>
                  <div class="form-group">
                    <label for="zipCode">ZIP Code *</label>
                    <input 
                      type="text" 
                      id="zipCode" 
                      v-model="shippingInfo.zipCode" 
                      required
                    >
                  </div>
                </div>

                <!-- Payment Method -->
                <div class="form-section">
                  <h2>Payment Method</h2>
                  
                  <div class="payment-methods">
                    <div class="payment-option">
                      <input 
                        type="radio" 
                        id="creditCard" 
                        name="paymentMethod" 
                        value="creditCard" 
                        v-model="paymentMethod"
                      >
                      <label for="creditCard">Credit Card</label>
                    </div>
                    <div class="payment-option">
                      <input 
                        type="radio" 
                        id="paypal" 
                        name="paymentMethod" 
                        value="paypal" 
                        v-model="paymentMethod"
                      >
                      <label for="paypal">PayPal</label>
                    </div>
                  </div>

                  <div v-if="paymentMethod === 'creditCard'" class="credit-card-form">
                    <div class="form-group">
                      <label for="cardNumber">Card Number *</label>
                      <input 
                        type="text" 
                        id="cardNumber" 
                        v-model="paymentInfo.cardNumber" 
                        placeholder="1234 5678 9012 3456"
                        required
                      >
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label for="expiryDate">Expiry Date *</label>
                        <input 
                          type="text" 
                          id="expiryDate" 
                          v-model="paymentInfo.expiryDate" 
                          placeholder="MM/YY"
                          required
                        >
                      </div>
                      <div class="form-group">
                        <label for="cvv">CVV *</label>
                        <input 
                          type="text" 
                          id="cvv" 
                          v-model="paymentInfo.cvv" 
                          placeholder="123"
                          required
                        >
                      </div>
                    </div>

                    <div class="form-group">
                      <label for="cardName">Name on Card *</label>
                      <input 
                        type="text" 
                        id="cardName" 
                        v-model="paymentInfo.cardName" 
                        required
                      >
                    </div>
                  </div>
                </div>

                <button type="submit" class="btn btn-large" :disabled="isProcessing">
                  {{ isProcessing ? 'Processing...' : `Complete Order - $${cartTotal.toLocaleString()}` }}
                </button>
              </form>
            </div>
          </div>

          <!-- Order Summary -->
          <div class="order-summary">
            <h2>Order Summary</h2>
            
            <div v-if="cartItems.length === 0" class="empty-cart">
              <p>Your cart is empty</p>
              <router-link to="/products" class="btn">Continue Shopping</router-link>
            </div>

            <div v-else>
              <div class="cart-items">
                <div 
                  class="cart-item" 
                  v-for="item in cartItems" 
                  :key="`${item.id}-${item.size}`"
                >
                  <img :src="item.image" :alt="item.name">
                  <div class="item-details">
                    <h4>{{ item.name }}</h4>
                    <p class="item-size">Size: {{ item.size }}</p>
                    <p class="item-price">${{ item.price.toLocaleString() }}</p>
                  </div>
                  <div class="item-quantity">
                    <button @click="updateQuantity(item, item.quantity - 1)" :disabled="item.quantity <= 1">
                      -
                    </button>
                    <span>{{ item.quantity }}</span>
                    <button @click="updateQuantity(item, item.quantity + 1)">
                      +
                    </button>
                  </div>
                  <div class="item-total">
                    ${{ (item.price * item.quantity).toLocaleString() }}
                  </div>
                  <button @click="removeItem(item)" class="remove-btn">×</button>
                </div>
              </div>

              <div class="order-totals">
                <div class="total-row">
                  <span>Subtotal:</span>
                  <span>${{ cartTotal.toLocaleString() }}</span>
                </div>
                <div class="total-row">
                  <span>Shipping:</span>
                  <span>Free</span>
                </div>
                <div class="total-row total">
                  <span>Total:</span>
                  <span>${{ cartTotal.toLocaleString() }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useCartStore } from '../stores/cart'
import { useRouter } from 'vue-router'
import { apiService } from '../services/api.js'

export default {
  name: 'Checkout',
  setup() {
    const cartStore = useCartStore()
    const router = useRouter()

    // Reactive data
    const isProcessing = ref(false)
    const paymentMethod = ref('creditCard')

    const shippingInfo = ref({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: ''
    })

    const paymentInfo = ref({
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardName: ''
    })

    // Computed properties
    const cartItems = computed(() => cartStore.cartItems)
    const cartTotal = computed(() => cartStore.cartTotal)

    // Methods
    const updateQuantity = (item, newQuantity) => {
      if (newQuantity > 0) {
        cartStore.updateQuantity(item.id, item.size, newQuantity)
      }
    }

    const removeItem = (item) => {
      cartStore.removeFromCart(item.id, item.size)
    }

    const processOrder = async () => {
      if (cartItems.value.length === 0) {
        alert('Your cart is empty!')
        return
      }

      isProcessing.value = true

      try {
        // Prepare order data
        const orderData = {
          items: cartItems.value.map(item => ({
            productId: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            size: item.size
          })),
          shipping: shippingInfo.value,
          payment: {
            method: paymentMethod.value,
            ...paymentInfo.value
          },
          total: cartTotal.value
        }

        // Send order to API
        const response = await apiService.createOrder(orderData)
        
        // Clear cart and redirect
        cartStore.clearCart()
        alert('Order placed successfully! Thank you for your purchase.')
        router.push('/')
      } catch (error) {
        console.error('Error processing order:', error)
        alert('There was an error processing your order. Please try again.')
      } finally {
        isProcessing.value = false
      }
    }

    return {
      isProcessing,
      paymentMethod,
      shippingInfo,
      paymentInfo,
      cartItems,
      cartTotal,
      updateQuantity,
      removeItem,
      processOrder
    }
  }
}
</script>

<style scoped>
.checkout-page {
  min-height: 100vh;
  background-color: var(--light-gray-bg);
}

.checkout-header {
  background-color: var(--white);
  padding: 40px 0;
  border-bottom: 1px solid var(--border-color);
}

.checkout-header h1 {
  margin-bottom: 0.5rem;
}

.checkout-content {
  padding: 60px 0;
}

.checkout-layout {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
}

.checkout-form {
  background: var(--white);
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}

.form-section {
  margin-bottom: 40px;
}

.form-section h2 {
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-dark);
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.form-group input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.payment-methods {
  margin-bottom: 20px;
}

.payment-option {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.payment-option input[type="radio"] {
  margin-right: 10px;
}

.credit-card-form {
  margin-top: 20px;
  padding: 20px;
  background: var(--light-gray-bg);
  border-radius: 8px;
}

.btn-large {
  width: 100%;
  padding: 16px 32px;
  font-size: 1.1rem;
  margin-top: 20px;
}

.order-summary {
  background: var(--white);
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  height: fit-content;
}

.order-summary h2 {
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
}

.empty-cart {
  text-align: center;
  padding: 40px 0;
}

.empty-cart p {
  margin-bottom: 1rem;
  color: var(--text-medium);
}

.cart-items {
  margin-bottom: 30px;
}

.cart-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px solid var(--border-color);
}

.cart-item:last-child {
  border-bottom: none;
}

.cart-item img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
}

.item-details {
  flex: 1;
}

.item-details h4 {
  margin-bottom: 0.25rem;
  font-size: 1rem;
}

.item-size {
  font-size: 0.9rem;
  color: var(--text-medium);
  margin-bottom: 0.25rem;
}

.item-price {
  font-weight: 600;
  color: var(--primary-color);
}

.item-quantity {
  display: flex;
  align-items: center;
  gap: 10px;
}

.item-quantity button {
  width: 30px;
  height: 30px;
  border: 1px solid var(--border-color);
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
}

.item-quantity button:hover:not(:disabled) {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.item-quantity button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.item-total {
  font-weight: 700;
  color: var(--text-dark);
  min-width: 80px;
  text-align: right;
}

.remove-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--error-color);
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn:hover {
  background: var(--error-color);
  color: white;
  border-radius: 4px;
}

.order-totals {
  border-top: 2px solid var(--border-color);
  padding-top: 20px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 1rem;
}

.total-row.total {
  font-weight: 700;
  font-size: 1.2rem;
  color: var(--primary-color);
  border-top: 1px solid var(--border-color);
  padding-top: 10px;
  margin-top: 10px;
}

@media (max-width: 768px) {
  .checkout-layout {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .cart-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .item-quantity {
    align-self: center;
  }
  
  .item-total {
    align-self: flex-end;
  }
}
</style> 